import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren} from "@angular/core";
import {Router} from "@angular/router";
import {interval, Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, map} from "rxjs/operators";
import {SubscriptionService} from "../service/subscription.service";
import {AuthService} from "../service/auth.service";
import {SubscriptionItem, SubscriptionRequest} from "../entities";
import {INTEGRATED_SCAC_CODES} from "../constants/carriers";
import {isValidContainerNumber} from "../utils/container";

@Component({
    selector: "app-home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  mode: 'search' | 'subscribe' = 'search';
  @ViewChildren('tabBtn') tabBtns!: QueryList<ElementRef<HTMLButtonElement>>;
  indicatorLeft = 0;
  indicatorWidth = 0;
  searchString = '';
  selectedScac = '';
  selectedRefType = 'equipmentNumber';
  refValue = '';
  refTouched = false;
  subRefValue = '';
  availableRefTypes = [
    {key: 'equipmentNumber', label: 'Container number'},
    {key: 'bookingNumber', label: 'Booking number'},
    {key: 'billNumber', label: 'Bill of lading'},
  ];
  carriers = INTEGRATED_SCAC_CODES;
  subscriptions: SubscriptionItem[] = [];
  page = 1;
  pageSize = 10;
  loading = false;
  loadingSubscriptions = false;
  errorMessage = '';
  successMessage = '';
  confirmDeleteSub: SubscriptionItem | null = null;
  deleting = false;
  deleteError = '';
  /** Current subscription-reference search term (empty = show the active list). */
  subSearch = '';
  private search$ = new Subject<string>();
  private searchSub?: Subscription;
  private refreshSub?: Subscription;
  private pollSub?: Subscription;
  /** How often the active subscriptions list silently refreshes (ms). */
  private static readonly POLL_INTERVAL_MS = 30_000;

  constructor(
    private router: Router,
    private subscriptionService: SubscriptionService,
    public authService: AuthService
  ) {
  }

  ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      this.loadSubscriptions();
    }
    this.refreshSub = this.subscriptionService.refresh.subscribe(() => {
      if (this.authService.isAuthenticated()) {
        this.loadSubscriptions();
      } else {
        this.subscriptions = [];
      }
    });
    // Debounce reference search input; an empty term falls back to the list.
    this.searchSub = this.search$.pipe(
      debounceTime(250),
      map(term => term.trim()),
      distinctUntilChanged()
    ).subscribe(() => {
      this.page = 1;
      this.loadSubscriptions();
    });
    // Silently refresh the active subscriptions list on a fixed interval.
    this.pollSub = interval(HomeComponent.POLL_INTERVAL_MS).subscribe(() => {
      if (this.authService.isAuthenticated()) {
        this.loadSubscriptions(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.pollSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateIndicator());
    this.tabBtns.changes.subscribe(() => setTimeout(() => this.updateIndicator()));
  }

  switchMode(next: 'search' | 'subscribe'): void {
    this.mode = next;
    this.errorMessage = '';
    setTimeout(() => this.updateIndicator());
  }

  private updateIndicator(): void {
    const btns = this.tabBtns?.toArray() ?? [];
    const idx = this.mode === 'search' ? 0 : 1;
    const btn = btns[idx]?.nativeElement ?? btns[0]?.nativeElement;
    if (btn) {
      this.indicatorLeft = btn.offsetLeft;
      this.indicatorWidth = btn.offsetWidth;
    }
  }

  /** True while a reference search is active (table shows matches, not the list). */
  get searchActive(): boolean {
    return this.subSearch.trim().length > 0;
  }

  /**
   * Load subscriptions for the current view: the consumer's open list, or — when
   * a reference search is active — prefix matches across all statuses.
   * @param silent when true (used by the 30s auto-refresh) the loading spinner is
   *        not shown and a failed request leaves the current list untouched, so the
   *        background refresh never flickers or blanks the table.
   */
  loadSubscriptions(silent = false): void {
    const term = this.subSearch.trim();
    const source$ = term
      ? this.subscriptionService.searchByReference(term)
      : this.subscriptionService.getAllSubscriptions('OPEN');
    if (!silent) this.loadingSubscriptions = true;
    source$.subscribe({
      next: (subscriptions) => {
        this.subscriptions = subscriptions.sort(
          (a, b) => new Date(b.subscriptionExpiryDate).getTime() - new Date(a.subscriptionExpiryDate).getTime()
        );
        this.loadingSubscriptions = false;
      },
      error: () => {
        if (!silent) this.subscriptions = [];
        this.loadingSubscriptions = false;
      }
    });
  }

  /** Feed each keystroke into the debounced search stream. */
  onSearchChange(term: string): void {
    this.search$.next(term);
  }

  /** Clear the search and return to the active subscriptions list. */
  clearSearch(): void {
    if (!this.subSearch) return;
    this.subSearch = '';
    this.page = 1;
    this.loadSubscriptions();
  }

  search(): void {
    const ref = this.searchString.trim();
    if (!ref) return;
    this.router.navigate(['/search', ref]);
  }

  hasSubscribeRef(): boolean {
    return this.refValue.trim().length > 0;
  }

  /** True when a container reference is entered but fails ISO 6346 validation. */
  get containerNumberInvalid(): boolean {
    return this.selectedRefType === 'equipmentNumber'
      && this.refValue.trim().length > 0
      && !isValidContainerNumber(this.refValue);
  }

  /** Only surface the validation error once the user has left the field. */
  get showContainerError(): boolean {
    return this.containerNumberInvalid && this.refTouched;
  }

  /** Cap container numbers at their fixed ISO 6346 length; no cap otherwise. */
  get refMaxLength(): number | null {
    return this.selectedRefType === 'equipmentNumber' ? 11 : null;
  }

  subscribe(): void {
    if (!this.hasSubscribeRef() || !this.selectedScac || this.containerNumberInvalid) return;

    this.errorMessage = '';
    this.successMessage = '';
    this.loading = true;
    const val = this.refValue.trim();
    const body: SubscriptionRequest = {
      scacCode: this.selectedScac,
    };
    if (this.selectedRefType === 'equipmentNumber') body.equipmentNumber = val;
    if (this.selectedRefType === 'bookingNumber') body.bookingNumber = val;
    if (this.selectedRefType === 'billNumber') body.billNumber = val;

    const subRef = this.subRefValue.trim();
    if (subRef) body.subscriptionReference = subRef;

    this.subscriptionService.createSubscription(body).subscribe({
      next: () => {
        this.loading = false;
        this.successMessage = 'Subscribed successfully — tracking data will be available shortly';
        this.selectedScac = '';
        this.selectedRefType = 'equipmentNumber';
        this.refValue = '';
        this.subRefValue = '';
        this.loadSubscriptions();
        // Return to the search tab so the user sees their new subscription in the list
        this.switchMode('search');
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err.error?.message || 'Failed to create subscription';
      }
    });
  }

  get pagedSubscriptions(): SubscriptionItem[] {
    const start = (this.page - 1) * this.pageSize;
    return this.subscriptions.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.subscriptions.length / this.pageSize);
  }

  subRef(sub: SubscriptionItem): string {
    return (sub.subscriptionReference || '').replace('SUBSCRIPTION#', '');
  }

  /** The reference kind used to create the subscription, derived from whichever
   *  identifier field is populated — a subscription is created with exactly one
   *  (see selectedRefType on subscribe). Precedence matches the value shown in
   *  the row so the label and value never disagree. */
  refTypeLabel(sub: SubscriptionItem): string {
    if (sub.equipmentNumber) return 'Container';
    if (sub.bookingNumber) return 'Booking';
    if (sub.billNumber) return 'Bill of lading';
    return '';
  }

  /** Short 4-letter code for the row's chip (full name shown on hover via title). */
  refTypeCode(sub: SubscriptionItem): string {
    if (sub.equipmentNumber) return 'CNTR';
    if (sub.bookingNumber) return 'BOOK';
    if (sub.billNumber) return 'BILL';
    return '';
  }

  /** True for subscriptions surfaced by search that are no longer active. */
  isClosed(sub: SubscriptionItem): boolean {
    return sub.subscriptionStatus === 'CLOSED';
  }

  /**
   * Single source of truth for how a subscription's connection is displayed.
   *
   * The label, the tooltip and the status dot all derive from this, so they
   * cannot drift apart. Previously the dot was driven by negated conditions in
   * the template while the text came from its own switch here, which is how
   * AWAITING_EQUIPMENT ended up rendering as a green "Active / Success - Carrier
   * Data Retrieved" the moment the backend started sending it.
   *
   * The returned value doubles as the dot's BEM modifier
   * (`status-dot--<state>`), so a new state needs a case here and a colour in
   * home.component.scss, and nothing else.
   */
  connectionState(
    sub: SubscriptionItem
  ): 'closed' | 'connecting' | 'awaiting-equipment' | 'no-connection' | 'open' {
    if (this.isClosed(sub)) return 'closed';
    switch (sub.connectionStatus) {
      case 'CONNECTING': return 'connecting';
      case 'AWAITING_EQUIPMENT': return 'awaiting-equipment';
      case 'NO_CONNECTION': return 'no-connection';
      // ACTIVE, and any legacy or future value, reads as connected. Leaving the
      // default permissive keeps older records (e.g. CONNECTED) displaying
      // sensibly rather than blank.
      default: return 'open';
    }
  }

  connectionLabel(sub: SubscriptionItem): string {
    switch (this.connectionState(sub)) {
      case 'closed': return 'Closed';
      case 'connecting': return 'Connecting';
      case 'awaiting-equipment': return 'Awaiting container';
      case 'no-connection': return 'No connection';
      default: return 'Active';
    }
  }

  connectionTooltip(sub: SubscriptionItem): string {
    switch (this.connectionState(sub)) {
      case 'closed':
        return 'Subscription Closed — No Longer Tracking';
      case 'connecting':
        return 'Subscription Created - Awaiting Tracking Data';
      case 'awaiting-equipment':
        return 'Booking Confirmed - Carrier Has Not Assigned A Container Yet';
      case 'no-connection':
        return 'No Carrier Data - Automatically Retrying For 24 Hours';
      default:
        return 'Success - Carrier Data Retrieved';
    }
  }

  trackSubscription(sub: SubscriptionItem): void {
    const ref = sub.equipmentNumber || sub.bookingNumber || sub.billNumber || '';
    if (ref) {
      this.router.navigate(['/search', ref]);
    }
  }

  promptDelete(sub: SubscriptionItem): void {
    this.deleteError = '';
    this.confirmDeleteSub = sub;
  }

  cancelDelete(): void {
    if (this.deleting) return;
    this.confirmDeleteSub = null;
  }

  confirmDelete(): void {
    const sub = this.confirmDeleteSub;
    if (!sub || this.deleting) return;

    this.deleting = true;
    this.deleteError = '';
    this.subscriptionService.deleteSubscription(this.subRef(sub)).subscribe({
      next: () => {
        this.deleting = false;
        this.confirmDeleteSub = null;
        this.successMessage = 'Subscription deleted';
        this.loadSubscriptions();
      },
      error: (err) => {
        this.deleting = false;
        this.deleteError = err.error?.message || 'Failed to delete subscription';
      }
    });
  }
}
